import {
  Box,
  Collapse,
  Divider,
  IconButton,
  InputBase,
  List,
  ListItemButton,
  Paper,
  Typography,
} from "@mui/material";
import { FC, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../hooks";
import {
  IMerchantResponse,
  IStoreResponse,
} from "../../../typings/interfaces/responses/all-merchants-response";
import { MasterSliceActions } from "../../../redux/slice/masterSlice";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import BusinessIcon from "@mui/icons-material/Business";
import StorefrontIcon from "@mui/icons-material/Storefront";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CheckIcon from "@mui/icons-material/Check";

const MerchantSection: FC<{
  merchant: IMerchantResponse;
  selectedStoreId?: number;
  onSelectStore(store: IStoreResponse, merchant: IMerchantResponse): void;
}> = ({ merchant, selectedStoreId, onSelectStore }) => {
  const [isOpen, setIsOpen] = useState(true);

  return (
    <Box sx={{ mb: "0.25rem" }}>
      {/* Merchant header */}
      <Box
        onClick={() => setIsOpen((prev) => !prev)}
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          p: "0.65rem 0.75rem",
          borderRadius: "0.5rem",
          cursor: "pointer",
          "&:hover": { backgroundColor: "action.hover" },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
          <BusinessIcon sx={{ color: "primary.main", fontSize: "1.8rem" }} />
          <Typography sx={{ fontSize: "1.3rem", fontWeight: 600 }}>
            {merchant.companyName}
          </Typography>
        </Box>
        <ExpandMoreIcon
          sx={{
            fontSize: "1.8rem",
            color: "text.secondary",
            transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.2s ease",
          }}
        />
      </Box>

      {/* Store list */}
      <Collapse in={isOpen}>
        <List disablePadding sx={{ pl: "1rem" }}>
          {merchant.stores.map((store) => {
            const isSelected = store.storeId === selectedStoreId;
            return (
              <ListItemButton
                key={store.storeId}
                onClick={() => onSelectStore(store, merchant)}
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  borderRadius: "0.5rem",
                  px: "0.75rem",
                  py: "0.55rem",
                  mb: "0.1rem",
                  backgroundColor: isSelected
                    ? "adminMenuItem.main"
                    : "transparent",
                  "&:hover": {
                    backgroundColor: isSelected
                      ? "adminMenuItem.main"
                      : "action.hover",
                  },
                }}
              >
                <Box
                  sx={{ display: "flex", alignItems: "center", gap: "0.65rem" }}
                >
                  <StorefrontIcon
                    sx={{
                      fontSize: "1.55rem",
                      color: isSelected ? "primary.main" : "text.secondary",
                    }}
                  />
                  <Typography
                    sx={{
                      fontSize: "1.25rem",
                      fontWeight: isSelected ? 600 : 400,
                      color: isSelected ? "primary.main" : "text.primary",
                    }}
                  >
                    {store.storeName}
                  </Typography>
                </Box>
                {isSelected && (
                  <CheckIcon
                    sx={{ fontSize: "1.55rem", color: "primary.main" }}
                  />
                )}
              </ListItemButton>
            );
          })}
        </List>
      </Collapse>
    </Box>
  );
};

export const StoreSelectorDrawerContent: FC = () => {
  const { merchantListResponse } = useAppSelector((state) => state.merchant);
  const { adminDashboardSelectedStore, adminDashboardSelectedMerchant } =
    useAppSelector((state) => state.master);
  const dispatch = useAppDispatch();
  const [search, setSearch] = useState("");

  const closeDrawer = () => {
    dispatch(MasterSliceActions.setStoreSelectorDrawerOpen(false));
  };

  const setSelectedStoreAndMerchant = (
    store: IStoreResponse,
    merchant: IMerchantResponse
  ) => {
    dispatch(MasterSliceActions.setAdminDashboardSelectedStore(store));
    dispatch(MasterSliceActions.setAdminDashboardSelectedMerchant(merchant));
    dispatch(MasterSliceActions.setStoreSelectorDrawerOpen(false));
  };

  const filteredMerchants = merchantListResponse?.body
    .map((merchant) => ({
      ...merchant,
      stores: merchant.stores.filter((s) =>
        s.storeName.toLowerCase().includes(search.toLowerCase())
      ),
    }))
    .filter(
      (merchant) =>
        merchant.stores.length > 0 ||
        merchant.companyName.toLowerCase().includes(search.toLowerCase())
    );

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          px: "1.5rem",
          py: "1rem",
        }}
      >
        <Typography sx={{ fontSize: "1.7rem", fontWeight: 700 }}>
          Select Store
        </Typography>
        <IconButton size="small" onClick={closeDrawer}>
          <CloseIcon sx={{ fontSize: "2rem" }} />
        </IconButton>
      </Box>

      {/* Currently selected */}
      {adminDashboardSelectedStore && (
        <Box
          sx={{
            mx: "1.5rem",
            mb: "0.75rem",
            px: "1rem",
            py: "0.65rem",
            borderRadius: "0.5rem",
            border: "0.071rem solid",
            borderColor: "primary.main",
            backgroundColor: "adminMenuItem.main",
            display: "flex",
            alignItems: "center",
            gap: "0.75rem",
          }}
        >
          <CheckIcon sx={{ color: "primary.main", fontSize: "1.6rem" }} />
          <Box>
            <Typography
              sx={{ fontSize: "1.1rem", color: "text.secondary", lineHeight: 1 }}
            >
              {adminDashboardSelectedMerchant?.companyName}
            </Typography>
            <Typography
              sx={{ fontSize: "1.3rem", fontWeight: 600, color: "primary.main" }}
            >
              {adminDashboardSelectedStore.storeName}
            </Typography>
          </Box>
        </Box>
      )}

      <Divider sx={{ mx: "1.5rem", mb: "0.75rem" }} />

      {/* Search */}
      <Box sx={{ px: "1.5rem", mb: "0.75rem" }}>
        <Paper
          elevation={0}
          sx={{
            display: "flex",
            alignItems: "center",
            border: "0.071rem solid",
            borderColor: "divider",
            borderRadius: "0.5rem",
            px: "0.85rem",
            py: "0.4rem",
          }}
        >
          <SearchIcon
            sx={{ color: "text.disabled", mr: "0.5rem", fontSize: "1.7rem" }}
          />
          <InputBase
            placeholder="Search store..."
            fullWidth
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ fontSize: "1.25rem" }}
          />
        </Paper>
      </Box>

      {/* Merchant / Store list */}
      <Box sx={{ flex: 1, overflowY: "auto", px: "1rem" }}>
        {filteredMerchants?.length === 0 ? (
          <Typography
            sx={{
              textAlign: "center",
              color: "text.secondary",
              fontSize: "1.2rem",
              mt: "2rem",
            }}
          >
            No stores found
          </Typography>
        ) : (
          filteredMerchants?.map((merchant) => (
            <MerchantSection
              key={merchant.merchantId}
              merchant={merchant}
              selectedStoreId={adminDashboardSelectedStore?.storeId}
              onSelectStore={setSelectedStoreAndMerchant}
            />
          ))
        )}
      </Box>
    </Box>
  );
};
